import "./index.css";
import "./FlowEditor.css";
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ProCard } from '@ant-design/pro-components';
import {
    Alert,
    Button,
    Modal,
    Form,
    Input,
    Space,
    Tabs,
    Select,
    Table,
    Tooltip,
    message
} from 'antd';
import { DeleteOutlined, SaveOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { BsFillPlayCircleFill } from "react-icons/bs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ModalAddAction from "./ModalAddAction";
import Trigger from "./Trigger";
import ActionRender from "./Actions";
import FlowCanvas from "./FlowCanvas";
import { EditTitleAction } from "./EditTitleAction";
import { connect } from "react-redux";
import {
    changeActionCurrentStep, deleteAutomation,
    removeActionFromAutomation,
    removeActionsFromAutomation,
    saveAutomationActionsAndTrigger,
    updateActionBranchFromAutomation,
    updateActionFromAutomation,
    updateBasicDataAutomation,
    updateNameActionFromAutomation
} from "../../../../hooks/automation";
import Snippets from "./Snippets";
import { findUnreachableSteps } from "./flowTree";
import { findStepsReferencingPosition, getAvailableBranches, getNameFromAction, getNameFromActionByType } from "./Actions/utils/functions";
import uuid from "react-uuid";
import { AutomationsLog } from "./Log";
import { automationAPI } from "services/actions/automations";

const { confirm } = Modal;

/* Largura do painel de configuração ajustada pelo usuário.
 *
 * Sem ajuste, vale o valor responsivo do CSS (clamp por vw). O arrasto é para
 * quem precisa de mais área num passo específico — script longo, filtros de
 * consulta — sem perder o fluxo de vista. Fica no localStorage porque é
 * preferência de espaço de trabalho, não dado da automação. */
const INSPECTOR_WIDTH_KEY = "automation:inspectorWidth";
const MIN_INSPECTOR_W = 360;
/* O canvas continua sendo a outra metade do trabalho: sem um mínimo, arrastar
 * até a borda deixaria o fluxo numa fresta ilegível. */
const MIN_CANVAS_W = 420;
const INSPECTOR_STEP = 24;

const clampInspectorWidth = (workspace, width) => {
    const max = Math.max(MIN_INSPECTOR_W, workspace.getBoundingClientRect().width - MIN_CANVAS_W);
    return Math.round(Math.min(max, Math.max(MIN_INSPECTOR_W, width)));
};

const readStoredInspectorWidth = () => {
    try {
        const stored = Number(window.localStorage.getItem(INSPECTOR_WIDTH_KEY));
        return Number.isFinite(stored) && stored >= MIN_INSPECTOR_W ? stored : null;
    } catch (error) {
        // Preferência é opcional: sem localStorage (modo restrito), cai no
        // valor responsivo em vez de quebrar a tela.
        return null;
    }
};

const storeInspectorWidth = (width) => {
    try {
        if (width === null) {
            window.localStorage.removeItem(INSPECTOR_WIDTH_KEY);
        } else {
            window.localStorage.setItem(INSPECTOR_WIDTH_KEY, String(width));
        }
    } catch (error) {
        // idem: não gravar a preferência não impede o ajuste desta sessão.
    }
};

/**
 * Por que este passo nunca executa. `causeIndex` é a posição que fecha o fluxo
 * (ver `findUnreachableSteps`), ou `null` quando o passo é uma junção que não
 * recebe origem nenhuma.
 */
const describeUnreachable = (actions, causeIndex) => {
    const cause = causeIndex === null || causeIndex === undefined
        ? null
        : actions[causeIndex];
    if (!cause) {
        return "Esta etapa nunca é executada: nenhum caminho chega até a junção que a antecede.";
    }
    const name = getNameFromAction(cause);
    if (cause.type === "switch") {
        // Os dois motivos juntos: os caminhos fecham E o desfecho de "nenhum
        // caminho casar" também encerra. Dizer só o primeiro faz o usuário
        // procurar o erro nos caminhos, quando o que destrava a continuação é
        // a terceira saída do desvio.
        return `Esta etapa nunca é executada: todos os caminhos do desvio "${name}" (passo ${causeIndex + 1}) terminam em "Encerrar fluxo", e o desvio também encerra a automação quando nenhum caminho casa. Para o fluxo seguir daqui, mude "Se nenhum caminho casar" para "Continuar no fluxo principal".`;
    }
    return `Esta etapa nunca é executada: "${name}" (passo ${causeIndex + 1}) encerra a automação antes de chegar aqui.`;
};

const FlowBuilder = ({
    automation,
    onSaveAutomationActionsAndTrigger,
    onChangeActionCurrentStep,
    onRemoveActionFromAutomation,
    onRemoveActionsFromAutomation,
    onUpdateNameActionFromAutomation,
    onUpdateActionBranchFromAutomation,
    onUpdateBasicDataAutomation,
    onUpdateActionFromAutomation,
    tab,
    hasPermissionToEdit = false,
    hasPermissionToDelete = false,
    hasPermissionToViewLog = false
}) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();


    const { actions, actionStep, loading } = automation;
    const [isOpenModalAddAction, setIsOpenModalAddAction] = useState(false);
    const workspaceRef = useRef(null);
    const [inspectorWidth, setInspectorWidth] = useState(readStoredInspectorWidth);
    const [isResizingInspector, setIsResizingInspector] = useState(false);
    const [isUnreachableModalOpen, setIsUnreachableModalOpen] = useState(false);

    // Alcance é propriedade do fluxo, não evento: recalculado a cada mudança na
    // lista, chega-se a ele por qualquer caminho (adicionar um "Encerrar
    // fluxo", mover um passo de caminho, remover outro, apagar uma rota).
    const unreachable = useMemo(() => findUnreachableSteps(actions), [actions]);
    // Contexto de inserção do canvas: depois de qual passo e em qual rota o
    // novo passo entra. Null = comportamento padrão (após o passo selecionado).
    const [addContext, setAddContext] = useState(null);

    const formItemLayout = {
        labelCol: {
            xs: {
                span: 24,
            },
            sm: {
                span: 8,
            },
        },
        wrapperCol: {
            xs: {
                span: 24,
            },
            sm: {
                span: 16,
            },
        },
    };
    const tailFormItemLayout = {
        wrapperCol: {
            xs: {
                span: 24,
                offset: 0,
            },
            sm: {
                span: 16,
                offset: 8,
            },
        },
    };

    const [formAction] = Form.useForm();
    const openAddModal = (context = null) => {
        setAddContext(context);
        setIsOpenModalAddAction(true);
    };

    const hideModal = () => {
        setAddContext(null);
        setIsOpenModalAddAction(false);
    };

    // Pointer events (não mouse): o mesmo código atende mouse, caneta e toque, e
    // os listeners ficam no documento para o arrasto não se perder quando o
    // ponteiro sai da divisória — que tem 16px de largura.
    const startResizeInspector = useCallback((event) => {
        const workspace = workspaceRef.current;
        if (!workspace) return;
        event.preventDefault();
        setIsResizingInspector(true);

        let latest = null;
        const handleMove = (moveEvent) => {
            // O painel é a coluna da direita: a largura é a distância do
            // ponteiro até a borda direita do espaço de trabalho.
            const { right } = workspace.getBoundingClientRect();
            latest = clampInspectorWidth(workspace, right - moveEvent.clientX);
            setInspectorWidth(latest);
        };
        const stop = () => {
            document.removeEventListener("pointermove", handleMove);
            document.removeEventListener("pointerup", stop);
            document.removeEventListener("pointercancel", stop);
            setIsResizingInspector(false);
            if (latest !== null) storeInspectorWidth(latest);
        };

        document.addEventListener("pointermove", handleMove);
        document.addEventListener("pointerup", stop);
        document.addEventListener("pointercancel", stop);
    }, []);

    // Teclado: a divisória é focável, então setas ajustam de 24 em 24px.
    const nudgeInspectorWidth = useCallback((delta) => {
        const workspace = workspaceRef.current;
        const inspector = workspace ? workspace.querySelector(".ae-inspector") : null;
        if (!workspace || !inspector) return;
        const next = clampInspectorWidth(workspace, inspector.getBoundingClientRect().width + delta);
        setInspectorWidth(next);
        storeInspectorWidth(next);
    }, []);

    // Duplo clique devolve a largura responsiva (o clamp por vw do CSS).
    const resetInspectorWidth = useCallback(() => {
        setInspectorWidth(null);
        storeInspectorWidth(null);
    }, []);

    const handleResizerKeyDown = (event) => {
        if (event.key === "ArrowLeft") {
            event.preventDefault();
            nudgeInspectorWidth(INSPECTOR_STEP);
        } else if (event.key === "ArrowRight") {
            event.preventDefault();
            nudgeInspectorWidth(-INSPECTOR_STEP);
        } else if (event.key === "Home") {
            event.preventDefault();
            resetInspectorWidth();
        }
    };

    const removeActions = () => {
        // Quem usa o resultado desta etapa fica com uma referência sem destino:
        // as posições seguintes são reescritas sozinhas, mas a referência à
        // etapa removida só o usuário sabe para onde deveria apontar.
        const dependents = findStepsReferencingPosition(actions, actionStep + 1)
            .filter((index) => index !== actionStep)
            .map((index) => `"${getNameFromAction(actions[index])}"`);

        confirm({
            title: "Deseja remover essa ação?",
            icon: <ExclamationCircleOutlined />,
            okText: "Ok",
            cancelText: "Cancelar",
            content: dependents.length
                ? `Toda configuração dessa ação será perdida! Além disso, ${dependents.length === 1 ? "a etapa" : "as etapas"} ${dependents.join(", ")} ${dependents.length === 1 ? "usa o resultado" : "usam o resultado"} dela — a referência ficará inválida e precisará ser corrigida antes de salvar.`
                : "Toda configuração dessa ação será perdida!",
            onOk: () => onRemoveActionFromAutomation(actionStep)
        })
    }

    // Rota (branch) do passo atual: só rotas declaradas por desvios anteriores
    // podem ser escolhidas; sem rota, o passo executa sempre (convergência).
    const renderBranchSelector = () => {
        if (actionStep === -1) return null;
        const action = actions[actionStep];
        if (!action) return null;

        const availableBranches = getAvailableBranches(actions, actionStep);
        if (availableBranches.length === 0 && !action.branch) return null;

        const options = [
            { value: "", label: "Sempre executa (fora dos caminhos)" },
            ...availableBranches.map((item) => ({
                value: item.value,
                label: `${item.label} — ${item.switchName}`,
            })),
        ];
        // Caminho órfão (desvio removido/movido): mantém visível para corrigir.
        if (action.branch && !availableBranches.some((item) => item.value === action.branch)) {
            options.push({ value: action.branch, label: `${action.branch} (caminho inexistente!)` });
        }

        return (
            <div className="ae-section">
                <div className="ae-section__label">Execução</div>
                <div className="ae-field">
                    <label className="ae-label">Executar somente no caminho</label>
                    <Select
                        style={{ width: "100%" }}
                        value={action.branch ?? ""}
                        options={options}
                        onChange={(value) => onUpdateActionBranchFromAutomation(actionStep, value === "" ? null : value)}
                    />
                    <div className="ae-hint">
                        Sem caminho, o passo executa sempre — inclusive fora dos desvios.
                    </div>
                </div>
            </div>
        );
    }

    const handleRenderStep = () => {
        if (actionStep === -1) {
            return <Trigger />
        }

        const action = actions[actionStep];
        if (!action) {
            return (
                <div className="ae-inspector__empty">
                    Selecione um passo no fluxo para configurá-lo.
                </div>
            );
        }

        return (<>
            {unreachable.has(actionStep) ? (
                <Alert
                    className="ae-alert"
                    type="warning"
                    showIcon
                    message="Etapa fora do fluxo de execução"
                    description={describeUnreachable(actions, unreachable.get(actionStep))}
                />
            ) : null}
            {renderBranchSelector()}
            <div className="ae-section">
                <div className="ae-section__label">Configuração</div>
                <ActionRender key={actionStep} action={action} formAction={formAction}
                    saveDataFromActionAfterChangeStep={saveDataFromActionAfterChangeStep} />
            </div>
        </>)
    }

    const getTitleStep = () => {
        if (actionStep === -1) {
            return "Editar Gatilho"
        } else {
            return getNameFromAction(actions[actionStep]);
        }
    }

    const getSubtitleStep = () => {
        if (actionStep === -1) {
            return "Defina as condições para que a automação seja iniciada."
        } else {
            return actions[actionStep].description;
        }
    }

    /**
     * Cabeçalho do inspector: repete o ícone do nó selecionado (mesma linguagem
     * visual do canvas), o tipo do passo, o nome editável e a ação destrutiva —
     * assim fica claro qual passo do fluxo o painel está configurando.
     */
    const renderInspectorHead = () => {
        const isTrigger = actionStep === -1;
        const action = isTrigger ? null : actions[actionStep];
        if (!isTrigger && !action) return null;

        return (
            <div className="ae-inspector__head">
                <div className="ae-inspector__icon">
                    {isTrigger ? <BsFillPlayCircleFill /> : action.icon}
                </div>
                <div className="ae-inspector__meta">
                    <div className="ae-inspector__kicker">
                        {isTrigger ? "Gatilho" : getNameFromActionByType(action.type)}
                    </div>
                    {isTrigger ? (
                        <div className="ae-title--static">Editar Gatilho</div>
                    ) : (
                        // key: sem ela o nome digitado num passo vazaria para o
                        // próximo passo selecionado (estado interno preservado).
                        <EditTitleAction
                            key={actionStep}
                            title={getTitleStep()}
                            name={action?.name ?? ""}
                            onChange={(value) => onUpdateNameActionFromAutomation(actionStep, value)}
                        />
                    )}
                    <div className="ae-inspector__desc">{getSubtitleStep()}</div>
                </div>
                {!isTrigger && hasPermissionToEdit ? (
                    <Tooltip title="Excluir este passo">
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={removeActions} />
                    </Tooltip>
                ) : null}
            </div>
        );
    }

    const { automation: automationData } = automation

    const initialValues = automationData ? {
        name: automationData.name,
        description: automationData.description,
        active: automationData.active,
    } : {};

    const confirmSave = () => {
        confirm({
            title: "Deseja salvar dados dessa automação?",
            icon: <SaveOutlined />,
            content: "Toda configuração atual será salva!",
            okText: "Ok",
            cancelText: "Cancelar",
            onOk: () => {
                onSaveAutomationActionsAndTrigger()
            }
        })
    }

    const saveActions = () => {
        const trigger = automation.trigger;
        if (trigger && trigger.type === 'multiple_row_action') {
            if (!trigger.config || !trigger.config.multipleTrigger || trigger.config.multipleTrigger.length === 0) {
                message.error("Selecione pelo menos uma ação no gatilho para iniciar essa automação.");
                return;
            }
        }

        // Etapas mortas são oferecidas para remoção aqui, e não no momento em
        // que passam a ser inalcançáveis: no salvar o fluxo está inteiro, e a
        // remoção — que renumera etapas e reescreve referências — é uma escolha
        // deliberada, não um modal no meio da montagem.
        if (unreachable.size > 0) {
            setIsUnreachableModalOpen(true);
            return;
        }

        confirmSave();
    }

    const unreachableSteps = [...unreachable.keys()].sort((a, b) => a - b);

    const saveKeepingUnreachable = () => {
        setIsUnreachableModalOpen(false);
        confirmSave();
    }

    const removeUnreachableAndSave = () => {
        setIsUnreachableModalOpen(false);
        onRemoveActionsFromAutomation(unreachableSteps);
        onSaveAutomationActionsAndTrigger();
    }

    const removeAutomation = () => {
        confirm({
            title: "Deseja excluir essa automação?",
            icon: <DeleteOutlined color={"red"} />,
            content: "Todos os dados do gatilho, das ações e dos logs serão excluídos!",
            okText: "Ok",
            okType: "danger",
            cancelText: "Cancelar",
            onOk: () => {
                dispatch(deleteAutomation(automation.automation.id, navigate));
            }
        })
    }

    const renderBtnSaveActions = () => {
        return (
            <React.Fragment>
                {hasPermissionToEdit ? (<Button icon={<SaveOutlined />} onClick={saveActions} type={"primary"}>Salvar Dados</Button>) : null}
            </React.Fragment>
        )
    }

    const saveDataFromActionAfterChangeStep = () => {
        // Só há o que salvar se a posição selecionada corresponde a uma etapa
        // existente: o inspector mostra o estado vazio quando não corresponde, e
        // gravar aí escreveria num passo que não existe.
        if (actionStep > -1 && actions[actionStep]) {
            const formData = formAction.getFieldsValue();
            const data = formData
            onUpdateActionFromAutomation({ step: actionStep, data })
        }
    }

    const handlerChangeActionCurrentStep = (step) => {
        saveDataFromActionAfterChangeStep();
        onChangeActionCurrentStep(step)
    }

    const mergeServices = (allServices, automationServices) => {
        return allServices.map(service => {
            const found = automationServices?.find(
                s => s.serviceId.id === service.id
            );

            return {
                id: service.id,
                name: service.serviceName,
                internalName: service.internalName,
                active: found ? found.active : false
            }
        })
    }

    //
    const [services, setServices] = useState([]);
    const [tempServices, setTempServices] = useState([]);
    const openServiceModal = () => {
        setTempServices(services.map(item => ({ ...item })));
        setIsServiceModalOpen(true);
    }


    //
    useEffect(() => {
        const loadServices = async () => {
            const allServices = await automationAPI.getAllServices();
            const automationServices = automationData?.internalServices ?? [];
            const mergedData = mergeServices(allServices?.data ?? [], automationServices)
            setServices(mergedData)
        }

        if (automationData?.internalServices) {
            loadServices();
        }
    }, [automationData])

    const [form] = Form.useForm();
    const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);

    const toggleService = (id) => {
        setTempServices(prev =>
            prev.map(s =>
                s.id === id ? { ...s, active: !s.active } : s
            )
        );
    };

    const columns = [
        {
            title: "Nome interno",
            dataIndex: "internalName",
            key: "internalName"
        },
        {
            title: "Serviço",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Status",
            key: "active",
            render: (_, record) => (
                <Button
                    type={record.active ? "primary" : "default"}
                    onClick={() => toggleService(record.id)}
                >
                    {record.active ? "Ativado" : "Desativado"}
                </Button>
            )
        }
    ];

    let defaultActiveKey = "2";
    if (tab) {
        switch (tab) {
            case "basic-data":
                defaultActiveKey = "1";
                break;
            case "actions":
                defaultActiveKey = "2";
                break;
            case "logs":
                defaultActiveKey = "3";
                break;
            default:
                defaultActiveKey = "1";
                break;
        }
    }

    const tabLogAutomaitons = hasPermissionToViewLog ? {
        key: "3",
        label: "Histórico de Execução",
        children: !loading && automationData && <AutomationsLog automation={automationData} />,
    } : null
    return (<>
        <ProCard loading={loading}>
            <Tabs defaultActiveKey={defaultActiveKey} items={[
                {
                    key: "1",
                    label: "Configurações Básicas",
                    children: !loading && automationData &&
                        <Form
                            {...formItemLayout}
                            name="register"
                            scrollToFirstError
                            autoComplete="off"
                            initialValues={initialValues}
                            onFinish={(values) => {
                                const finalValues = {
                                    ...values,
                                    internalServices: services.map(item => ({
                                        serviceId: item.id,
                                        active: item.active
                                    }))
                                }
                                onUpdateBasicDataAutomation(automationData.id, finalValues)
                            }}
                            form={form}
                        >
                            <Form.Item
                                name="name"
                                label="Nome da Automação"
                                rules={[
                                    {
                                        required: true,
                                        message: "Por favor insira um nome para esta automação!",
                                        whitespace: true,
                                    }
                                ]}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="description"
                                label="Descrição da Automação"
                                tooltip={"Qual o propósito dessa automação"}
                                rules={[
                                    {
                                        required: false,
                                        message: "Por favor insira uma descrição dessa automação, não pode terminar em espaço em branco!",
                                        whitespace: true,
                                    }
                                ]}
                            >
                                <Input.TextArea />
                            </Form.Item>
                            <Form.Item
                                name="active"
                                label="Ativado"
                                tooltip={"Defina se automação está ativa ou não!"}
                            >
                                <Select>
                                    <Select.Option value={true}>Sim</Select.Option>
                                    <Select.Option value={false}>Não</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item {...tailFormItemLayout}>
                                <Button
                                    type="ghost"
                                    danger
                                    onClick={openServiceModal}
                                >
                                    Definir serviços
                                </Button>
                                &nbsp;
                                &nbsp;
                                <Button type="default" htmlType="reset">
                                    Limpar
                                </Button>
                                &nbsp;
                                &nbsp;
                                {hasPermissionToEdit ? (
                                    <Button type="primary" htmlType="submit">
                                        Salvar
                                    </Button>) : null}
                                &nbsp;
                                &nbsp;
                                {hasPermissionToDelete ? (<Button icon={<DeleteOutlined />} onClick={removeAutomation} danger>Excluir Automação</Button>) : null}
                            </Form.Item>
                        </Form>,
                },
                {
                    key: "2",
                    label: "Ações da Automação",
                    children: (
                        <div
                            ref={workspaceRef}
                            className={`ae-workspace${isResizingInspector ? " ae-workspace--resizing" : ""}`}
                            // Só entra na árvore quando há ajuste do usuário:
                            // sem a variável, o CSS usa o valor responsivo.
                            style={inspectorWidth ? { "--ae-inspector-user": `${inspectorWidth}px` } : undefined}
                        >
                            <section className="ae-canvas">
                                <div className="ae-bar">
                                    <span className="ae-bar__title">Fluxo</span>
                                    <span className="ae-bar__hint">
                                        Clique em um passo para configurá-lo
                                    </span>
                                    <span className="ae-bar__spacer" />
                                    {renderBtnSaveActions()}
                                </div>
                                <FlowCanvas
                                    actions={actions}
                                    actionStep={actionStep}
                                    trigger={automation.trigger}
                                    hasPermissionToEdit={hasPermissionToEdit}
                                    onSelect={handlerChangeActionCurrentStep}
                                    onAdd={(context) => openAddModal(context)}
                                />
                            </section>
                            <div
                                className={`ae-resizer${isResizingInspector ? " ae-resizer--active" : ""}`}
                                role="separator"
                                aria-orientation="vertical"
                                aria-label="Ajustar a largura do painel de configuração"
                                title="Arraste para redimensionar — duplo clique volta ao padrão"
                                tabIndex={0}
                                onPointerDown={startResizeInspector}
                                onDoubleClick={resetInspectorWidth}
                                onKeyDown={handleResizerKeyDown}
                            />
                            <aside className="ae-inspector">
                                {renderInspectorHead()}
                                <div className="ae-inspector__body">
                                    {handleRenderStep()}
                                </div>
                            </aside>
                        </div>
                    ),
                },
                ...(tabLogAutomaitons ? [tabLogAutomaitons] : []),
            ]} />
        </ProCard>
        <ModalAddAction isModalOpen={isOpenModalAddAction} hideModal={hideModal}
            addContext={addContext}
            saveDataFromActionAfterChangeStep={saveDataFromActionAfterChangeStep} />
        <Modal
            open={isUnreachableModalOpen}
            onCancel={() => setIsUnreachableModalOpen(false)}
            title="Etapas que nunca serão executadas"
            width={620}
            footer={(
                <Space>
                    <Button onClick={() => setIsUnreachableModalOpen(false)}>Cancelar</Button>
                    <Button onClick={saveKeepingUnreachable}>Salvar mesmo assim</Button>
                    <Button type="primary" danger onClick={removeUnreachableAndSave}>
                        {unreachableSteps.length === 1
                            ? "Remover a etapa e salvar"
                            : `Remover as ${unreachableSteps.length} etapas e salvar`}
                    </Button>
                </Space>
            )}
        >
            <p>
                O fluxo já se encerra antes destas etapas, então elas nunca serão
                executadas:
            </p>
            <ul className="ae-unreachable">
                {unreachableSteps.map((step) => (
                    <li key={step}>
                        <strong>Passo {step + 1} — {getNameFromAction(actions[step])}</strong>
                        <div className="ae-unreachable__why">
                            {describeUnreachable(actions, unreachable.get(step))}
                        </div>
                    </li>
                ))}
            </ul>
            <p className="ae-hint">
                Remover renumera as etapas seguintes; as referências por posição
                (<code>steps.N</code>) são reescritas junto. Salvar sem remover também
                funciona — as etapas ficam gravadas, apenas sem executar.
            </p>
        </Modal>
        <Snippets formAction={formAction} />
        <Modal
            title="Reflexões das automações por ações internas do sistema"
            open={isServiceModalOpen}
            onCancel={() => setIsServiceModalOpen(false)}
            onOk={() => {
                setServices(tempServices);
                setIsServiceModalOpen(false);
            }}
            width={600}
        >
            <p>
                Com o serviço desativado, as alterações de registro feitas por
                esta automação deixam de acionar a varredura que procura os
                registros relacionados e reflete o novo valor nos outros
                formulários. A automação continua sendo disparada e executando
                todos os passos normalmente.
            </p>
            <Table
                dataSource={tempServices}
                columns={columns}
                rowKey="id"
                pagination={false}
            />
            <p className="ae-hint">
                A escolha só é gravada ao salvar as Configurações Básicas.
            </p>
        </Modal>
    </>);
};
const mapStateToProps = ({ automation }) => ({
    automation: automation,
});

const mapDispatchToProps = (dispatch) => {
    return {
        onSaveAutomationActionsAndTrigger: () => dispatch(saveAutomationActionsAndTrigger()),
        onChangeActionCurrentStep: (step) => dispatch(changeActionCurrentStep(step)),
        onRemoveActionFromAutomation: (step) => dispatch(removeActionFromAutomation(step)),
        onRemoveActionsFromAutomation: (steps) => dispatch(removeActionsFromAutomation(steps)),
        onUpdateNameActionFromAutomation: (step, name) => dispatch(updateNameActionFromAutomation(step, name)),
        onUpdateActionBranchFromAutomation: (step, branch) => dispatch(updateActionBranchFromAutomation(step, branch)),
        onUpdateBasicDataAutomation: (id, data) => {
            const payload = {
                name: data.name,
                description: data.description,
                active: data.active,
                internalServices: data?.internalServices ?? []
            }
            dispatch(updateBasicDataAutomation(id, payload))
            window.location.reload();
        },
        onUpdateActionFromAutomation: ({ step, data }) => dispatch(updateActionFromAutomation({ step, data }))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(FlowBuilder);
